import { useNavigate } from 'react-router-dom';
import {
  ClockCircleOutline,
  ExclamationCircleOutline,
  StarFill,
} from 'antd-mobile-icons';
import DashboardPackCard, {
  type PackCardAction,
  type PackCardMetric,
  type PackCardNotice,
  type PackCardQueueItem,
  type PackCardTone,
} from '../molecules/DashboardPackCard';
import { scooterPackService } from '../../services/api/scooterPackService';
import type { ScooterPack } from '../../services/api/scooterPackService';
import { formatRemainingHours, formatValidityHours } from '../../utils/packValidity';

interface FleetScooterPackCardProps {
  activePack: ScooterPack | null;
  queuedPacks: ScooterPack[];
  loading?: boolean;
  error?: string | null;
  onPurchaseClick?: () => void;
  onRetry?: () => void;
}

const formatStatus = (status: string) => {
  switch (status.toUpperCase()) {
    case 'ACTIVE':
      return 'Active';
    case 'RESERVED':
      return 'Queued';
    case 'EXPIRED':
      return 'Expired';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return status;
  }
};

const formatPackType = (packType: string) => {
  switch (packType.toUpperCase()) {
    case 'HYBRID':
      return 'Time + distance';
    case 'DISTANCE_ONLY':
      return 'Distance only';
    case 'TIME_ONLY':
      return 'Time only';
    default:
      return packType;
  }
};

const formatLimit = (pack: ScooterPack) => {
  const details: string[] = [formatPackType(pack.pack_type)];
  const validityLabel = formatValidityHours(pack.validity_hours);

  if (pack.distance_limit_km) details.push(`${pack.distance_limit_km} km`);
  if (validityLabel) details.push(validityLabel);

  return details.join(' / ');
};

const buildQueueItems = (queuedPacks: ScooterPack[]): PackCardQueueItem[] =>
  queuedPacks.slice(0, 3).map(pack => ({
    key: pack.pack_id,
    title: pack.template_name || 'Scooter Pack',
    detail: formatLimit(pack),
    badge: pack.designated_next
      ? (
        <>
          <StarFill style={{ marginRight: 4 }} />
          NEXT
        </>
      )
      : undefined,
  }));

const FleetScooterPackCard = ({
  activePack,
  queuedPacks,
  loading = false,
  error,
  onPurchaseClick,
  onRetry,
}: FleetScooterPackCardProps) => {
  const navigate = useNavigate();
  const queueItems = buildQueueItems(queuedPacks);
  const queueOverflowCount = Math.max(queuedPacks.length - queueItems.length, 0);
  const fallbackActions: PackCardAction[] = [
    ...(onPurchaseClick
      ? [{
        key: 'buy',
        label: 'Buy Scooter Pack',
        onClick: onPurchaseClick,
      }]
      : []),
    ...(onRetry
      ? [{
        key: 'retry',
        label: 'Retry',
        fill: 'outline' as const,
        onClick: onRetry,
      }]
      : []),
  ];

  if (loading) {
    return (
      <DashboardPackCard
        title="Scooter Pack"
        icon={<span>Km</span>}
        statusLabel="Loading"
        statusTone="blue"
        emptyState={{
          title: 'Loading scooter pack',
          description: 'Checking the current scooter pack for this vehicle.',
        }}
        actions={onPurchaseClick ? fallbackActions.filter(action => action.key === 'buy') : []}
      />
    );
  }

  if (error) {
    return (
      <DashboardPackCard
        title="Scooter Pack"
        icon={<span>Km</span>}
        statusLabel="Error"
        statusTone="red"
        emptyState={{
          icon: <ExclamationCircleOutline />,
          title: 'Scooter pack unavailable',
          description: error,
        }}
        actions={fallbackActions}
      />
    );
  }

  if (!activePack) {
    return (
      <DashboardPackCard
        title="Scooter Pack"
        icon={<span>Km</span>}
        statusLabel="No Pack"
        statusTone="grey"
        emptyState={{
          title: 'No active scooter pack',
          description: queuedPacks.length > 0
            ? `You have ${queuedPacks.length} scooter pack${queuedPacks.length === 1 ? '' : 's'} waiting to activate.`
            : 'Fleet riders need a scooter pack for this vehicle.',
          action: onPurchaseClick
            ? {
              label: 'Buy Scooter Pack',
              onClick: onPurchaseClick,
            }
            : undefined,
        }}
        queueTitle={queuedPacks.length > 0 ? 'Queued scooter packs' : undefined}
        queueItems={queueItems}
        queueOverflowCount={queueOverflowCount}
      />
    );
  }

  const packStatus = scooterPackService.calculatePackStatus(activePack);
  const tone: PackCardTone = packStatus.statusColor;
  const totalValidityLabel = formatValidityHours(activePack.validity_hours);

  const metrics: PackCardMetric[] = [
    ...(activePack.distance_limit_km && packStatus.distanceRemaining !== null
      ? [{
        key: 'distance',
        icon: <span>Km</span>,
        label: 'Distance Remaining',
        value: `${packStatus.distanceRemaining.toFixed(1)} km`,
        detail: `of ${activePack.distance_limit_km} km`,
        percent: (packStatus.distanceRemaining / activePack.distance_limit_km) * 100,
        tone,
      }]
      : []),
    ...(activePack.validity_hours && packStatus.hoursRemaining !== null
      ? [{
        key: 'time',
        icon: <ClockCircleOutline />,
        label: 'Time Left',
        value: formatRemainingHours(packStatus.hoursRemaining),
        detail: `of ${totalValidityLabel} total`,
        percent: (packStatus.hoursRemaining / activePack.validity_hours) * 100,
        tone,
      }]
      : []),
  ];

  const notice: PackCardNotice | undefined = packStatus.isExpiringSoon
    ? {
      tone: 'warning',
      icon: <ClockCircleOutline />,
      content: `Scooter pack expires in ${formatRemainingHours(packStatus.hoursRemaining)}. Buy a new pack to keep riding.`,
    }
    : undefined;

  const actions: PackCardAction[] = [
    ...(onPurchaseClick
      ? [{
        key: 'buy',
        label: 'Buy More',
        onClick: onPurchaseClick,
      }]
      : []),
    {
      key: 'view',
      label: 'View Scooter Packs',
      fill: 'outline',
      onClick: () => navigate('/packs?type=scooter'),
    },
  ];

  return (
    <DashboardPackCard
      title="Scooter Pack"
      icon={<span>Km</span>}
      statusLabel={formatStatus(activePack.status)}
      statusTone={tone}
      packName={activePack.template_name || 'Scooter Pack'}
      packMeta={formatPackType(activePack.pack_type)}
      metrics={metrics}
      notice={notice}
      queueTitle={queuedPacks.length > 0 ? `Next: ${queuedPacks.length} scooter pack${queuedPacks.length === 1 ? '' : 's'} queued` : undefined}
      queueItems={queueItems}
      queueOverflowCount={queueOverflowCount}
      actions={actions}
    />
  );
};

export default FleetScooterPackCard;
